/** Static errorMsg strings from leko-tech-api/api/core/ebay.js (+ listing guard). */
const EXACT = {
  "Institution was not found.": "parts.inventory.ebay_errors.institution_not_found",
  "This institution does not have eBay export enabled.":
    "parts.inventory.ebay_errors.export_not_enabled",
  "This institution does not have eBay Details.":
    "parts.inventory.ebay_errors.no_ebay_details",
  "Part was not found.": "parts.inventory.ebay_errors.part_not_found",
  "Part was not found": "parts.inventory.ebay_errors.part_not_found",
  "This part is not marked for export to eBay.":
    "parts.inventory.ebay_errors.not_export",
  "Unable to verify eBay part-code exclusions. The listing was refused.":
    "parts.inventory.ebay_errors.exclusion_verify_failed",
  "Cannot add item on eBay. No Title found for this item":
    "parts.inventory.ebay_errors.no_title",
  "Cannot add item on eBay. Price should be more than 1":
    "parts.inventory.ebay_errors.price_low",
  "Cannot add item on eBay. No Shipping Cost":
    "parts.inventory.ebay_errors.no_shipping",
  "Cannot add item on eBay. No car and no applicability found.":
    "parts.inventory.ebay_errors.no_car_applicability",
  "Cannot add item on eBay. Could not find shipping policy (missing weight range).":
    "parts.inventory.ebay_errors.no_shipping_policy",
  "Cannot add item on eBay. A listing attempt for this part and site is already in progress.":
    "parts.inventory.ebay_errors.listing_in_progress",
  "Cannot add item on eBay. Part notes position (SX/DX) do not match the InfoCar catalog position. Update the part notes or change the InfoCar position before listing.":
    "parts.inventory.ebay_position_mismatch",
  "eBayId does not exist for this part.":
    "parts.inventory.ebay_errors.ebay_id_missing",
  "Provided country does not have listings for this part.":
    "parts.inventory.ebay_errors.country_no_listings",
  "Cannot edit item on eBay. No Title found for this item":
    "parts.inventory.ebay_errors.edit_no_title",
  "Cannot edit item on eBay. No car and no applicability found.":
    "parts.inventory.ebay_errors.edit_no_car_applicability",
  "Cannot edit item on eBay. Price should be more than 1":
    "parts.inventory.ebay_errors.edit_price_low",
  "Cannot edit item on eBay. No Shipping Cost":
    "parts.inventory.ebay_errors.edit_no_shipping",
  "eBay account not linked for this institution. Please connect your eBay account to proceed.":
    "parts.inventory.ebay_errors.ebay_account_not_linked",
};

const PATTERNS = [
  {
    re: /^Adding items on eBay for the site '(.+)' is currently disabled\.$/,
    key: "parts.inventory.ebay_errors.site_disabled",
    params: (m) => ({ site: m[1] }),
  },
  {
    re: /^Cannot add item on eBay\. Part already exists on eBay for site '(.+)'$/,
    key: "parts.inventory.ebay_errors.already_exists_site",
    params: (m) => ({ site: m[1] }),
  },
  {
    re: /^The part with the title: (.+) cannot be added to eBay because it violates eBay's policy\.$/,
    key: "parts.inventory.ebay_errors.exclusion_policy",
    params: (m) => ({ title: m[1] }),
  },
  {
    re: /^The part with the title: (.+) cannot be added to eBay because it is restricted for (.+)\.$/,
    key: "parts.inventory.ebay_errors.exclusion_restricted",
    params: (m) => ({ title: m[1], restrictedFor: m[2] }),
  },
  {
    re: /^Unsupported site: (.+)\. Must be one of: (.+)$/,
    key: "parts.inventory.ebay_errors.unsupported_site",
    params: (m) => ({ site: m[1], allowed: m[2] }),
  },
];

const EDIT_PREFIX_IT =
  "Non è stato possibile aggiungere la parte su eBay: ";
const EDIT_PREFIX_EN = "Could not add the part to eBay:";

function resolveTranslation(t, i18nKey, rawFallback, values) {
  const out = t(i18nKey, values || {});
  if (!out || out === i18nKey) return rawFallback;
  return out;
}

/**
 * Normalize API / axios error payloads to a display string (unchanged text when unknown).
 */
export function coerceEbayErrorPayload(payload) {
  if (payload == null || payload === "") return "";
  if (typeof payload === "string") return payload;
  if (typeof payload === "number" || typeof payload === "boolean") {
    return String(payload);
  }
  if (typeof payload === "object") {
    if (typeof payload.errorMsg === "string") return payload.errorMsg;
    if (typeof payload.message === "string") return payload.message;
    if (typeof payload.error === "string") return payload.error;
    if (typeof payload.msg === "string") return payload.msg;
    if (Array.isArray(payload)) {
      return payload
        .map((entry) => coerceEbayErrorPayload(entry))
        .filter(Boolean)
        .join(" | ");
    }
    try {
      return JSON.stringify(payload);
    } catch {
      return String(payload);
    }
  }
  return String(payload);
}

function translateSingleEbayErrorMessage(msg, t) {
  if (!msg || typeof msg !== "string") return msg;
  const trimmed = msg.trim();
  const key = EXACT[trimmed];
  if (key) return resolveTranslation(t, key, trimmed);
  for (let i = 0; i < PATTERNS.length; i += 1) {
    const { re, key: patternKey, params } = PATTERNS[i];
    const match = trimmed.match(re);
    if (match) {
      return resolveTranslation(t, patternKey, trimmed, params(match));
    }
  }
  return msg;
}

/**
 * Maps known API eBay errorMsg values to portal i18n (API unchanged).
 * Unknown or untranslatable text is returned as-is from the API.
 * @param {string|*} msg
 * @param {(key: string, values?: object) => string} t - vue-i18n $t
 */
export function translateEbayErrorMessage(msg, t) {
  const raw = coerceEbayErrorPayload(msg);
  if (!raw) return raw;

  if (raw.startsWith(EDIT_PREFIX_IT)) {
    const rest = raw.slice(EDIT_PREFIX_IT.length);
    const prefix = resolveTranslation(
      t,
      "parts.inventory.ebay_errors.edit_prefix",
      EDIT_PREFIX_IT.trim(),
    );
    return `${prefix} ${translateEbayErrorMessage(rest, t)}`.trim();
  }
  if (raw.startsWith(EDIT_PREFIX_EN)) {
    const rest = raw.slice(EDIT_PREFIX_EN.length).trim();
    const prefix = resolveTranslation(
      t,
      "parts.inventory.ebay_errors.edit_prefix",
      EDIT_PREFIX_EN,
    );
    return `${prefix} ${translateEbayErrorMessage(rest, t)}`.trim();
  }

  if (raw.includes(" | ")) {
    return raw
      .split(" | ")
      .map((part) => translateSingleEbayErrorMessage(part.trim(), t))
      .join(" | ");
  }

  return translateSingleEbayErrorMessage(raw, t);
}
